'use client'
import Image from 'next/image'
import {useEffect, useState} from 'react'
import Link from 'next/link'
import type {Article} from '@/utils/types'
import {gql} from '@/libs/contentful/client'
import Filters from './Filters'
import {standardDateFormat} from '@/utils/dateFormatter'
import FadeUpOnVisible from '@/animations/FadeUpOnVisible'
import NewsCardSkeleton from './NewsCardSkeleton'

const getNews = async () =>
  (
    await gql(`
      {
        articleCollection (preview: ${process.env.NEXT_PUBLIC_CONTENTFUL_SHOW_PREVIEW ?? false}, order:publishedDate_DESC) {
          items {
            slug
            title
            category
            publishedDate
            coverImage {
              title
              url
            }
          }
        }
      }
    `)
  ).data?.articleCollection?.items as Article[]

const News = () => {
  const [news, setNews] = useState<Article[]>([])
  const [filteredNews, setFilteredNews] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchNews = async () => {
      const newsData = await getNews()
      setNews(newsData || [])
      setFilteredNews(newsData || [])
      setLoading(false)
    }
    fetchNews()
  }, [])

  return (
    <>
      <Filters filterNews={category => setFilteredNews(category === 'All news' ? news : news.filter(item => item.category === category))} />
      <FadeUpOnVisible className="mx-6 w-full grid-layout-default grid-sublayout lg:max-w-[1780.8px] pb-8 lg:pb-16" threshold={0}>
        {loading ? (
          Array.from(Array(3).keys()).map((_, i) => <NewsCardSkeleton key={i} />)
        ) : !filteredNews.length ? (
          <p className="h-[500px] text-center text-body-lg grid-sublayout-default">No news found</p>
        ) : (
          filteredNews.map(({slug, category, publishedDate, coverImage, title}) => (
            <Link
              key={slug}
              href={`/news/${slug}`}
              aria-label="Go to article"
              className="my-8 cursor-pointer gap-x-6 gap-y-8 grid-sublayout-default grid-sublayout md:items-center lg:my-16"
            >
              <Image
                src={coverImage.url}
                alt={coverImage.title ?? 'Article cover image'}
                placeholder="blur"
                blurDataURL={`${coverImage.url}?w=800&h=350&q=1`}
                priority
                width={80}
                height={35}
                sizes="(max-width: 767px) 100vw, (max-width: 1439px) 50vw, 42vw"
                className="col-span-4 aspect-video w-full object-cover lg:col-span-5"
              />
              <div className="col-span-4 md:col-span-3 md:col-start-6 lg:col-span-4 lg:col-start-7">
                <div className="flex gap-x-1 text-black-md text-body-xs">
                  <p>{category}</p>
                  <span>·</span>
                  <p>{standardDateFormat(publishedDate)}</p>
                </div>
                <h1 className="pt-4 text-headline-md lg:text-headline-lg">{title}</h1>
              </div>
            </Link>
          ))
        )}
      </FadeUpOnVisible>
    </>
  )
}

export default News
